import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-8">
          <Link to="/" className="text-gray-400 hover:text-white mr-4">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-3xl font-bold text-white">Terms & Conditions</h1>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 lg:p-8 border border-gray-700 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 mb-3">
              Welcome to ADAPT ("we," "our," or "us"). By accessing or using our platform, you agree to be bound by these Terms and Conditions ("Terms"). Please read them carefully.
            </p>
            <p className="text-gray-300">
              These Terms govern your access to and use of our services, including any content, functionality, and services offered on or through our website (the "Service").
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Accounts and Registration</h2>
            <p className="text-gray-300 mb-3">
              To access certain features of the Service, you may be required to register for an account. You must provide accurate, current, and complete information during the registration process and keep your account information up-to-date.
            </p>
            <p className="text-gray-300 mb-3">
              You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. We encourage you to use "strong" passwords (passwords that use a combination of upper and lower case letters, numbers, and symbols) with your account.
            </p>
            <p className="text-gray-300">
              You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Subscription and Payments</h2>
            <p className="text-gray-300 mb-3">
              Some aspects of the Service may be provided for a fee. You will be required to select a payment plan and provide accurate information regarding your billing method. You agree to pay all fees at the prices then in effect for your subscription.
            </p>
            <p className="text-gray-300 mb-3">
              Billing occurs on a recurring basis, depending on the subscription plan you select. You authorize us to charge your payment method for all fees incurred. All fees are exclusive of all taxes, levies, or duties imposed by taxing authorities.
            </p>
            <p className="text-gray-300">
              You may cancel your subscription at any time. Upon cancellation, you will continue to have access to the Service through the end of your billing period.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Intellectual Property</h2>
            <p className="text-gray-300 mb-3">
              The Service and its original content, features, and functionality are and will remain the exclusive property of ADAPT and its licensors. The Service is protected by copyright, trademark, and other laws of both the United Kingdom and foreign countries.
            </p>
            <p className="text-gray-300">
              Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of ADAPT.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. User Content</h2>
            <p className="text-gray-300 mb-3">
              Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("User Content"). You are responsible for the User Content that you post on or through the Service, including its legality, reliability, and appropriateness.
            </p>
            <p className="text-gray-300 mb-3">
              By posting User Content on or through the Service, you represent and warrant that: (i) the User Content is yours (you own it) and/or you have the right to use it and the right to grant us the rights and license as provided in these Terms, and (ii) that the posting of your User Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person or entity.
            </p>
            <p className="text-gray-300">
              We reserve the right to terminate the account of any user who infringes copyrights or other intellectual property rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Privacy Policy</h2>
            <p className="text-gray-300 mb-3">
              Please refer to our Privacy Policy for information about how we collect, use, and disclose information about you.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Termination</h2>
            <p className="text-gray-300 mb-3">
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
            </p>
            <p className="text-gray-300">
              If you wish to terminate your account, you may simply discontinue using the Service, or notify us that you wish to delete your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-300 mb-3">
              In no event shall ADAPT, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Changes to Terms</h2>
            <p className="text-gray-300 mb-3">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-300">
              By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about these Terms, please contact us at support@adapt-framework.com.
            </p>
          </section>

          <div className="pt-6 border-t border-gray-700 text-center">
            <p className="text-gray-400 text-sm">Last updated: June 15, 2025</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;